/**
 * useLessonBookingQuote — what a lesson would cost the signed-in member:
 * whether their package covers it, and what is owed if it does not. The only
 * source of money on the lesson payment step — nothing there computes a price.
 *
 * A read, despite the verb, so it is a query. Keyed under `lessons.all`:
 * buying a package or booking a lesson changes the balance, and both already
 * invalidate that prefix, which re-prices this.
 *
 * @module lessons/hooks/useLessonBookingQuote
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient/types';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { appRuntime } from '~/services/appRuntime';
import { quoteLessonBooking } from '../api/quoteLessonBooking';
import type { ApiLessonBookingQuotePayload } from '../types';

export interface UseLessonBookingQuoteParams {
  clubId: number | undefined;
  instructorId: number | null;
  bayId: number | null;
  /** ISO-8601 UTC. */
  startTime: string | null;
  endTime: string | null;
  enabled?: boolean;
}

export type UseLessonBookingQuoteResult = UseQueryResult<ApiResult<ApiLessonBookingQuotePayload>, Error> & {
  /** The server's answer — priced, or refused with reasons. Null until one arrives. */
  quote: ApiLessonBookingQuotePayload | null;
  /** An answer arrived at least once. */
  hasAnswer: boolean;
  /** The refusal envelope when the latest answer is `success: false`. */
  failure: ApiErrorResult | null;
};

export function useLessonBookingQuote({
  clubId,
  instructorId,
  bayId,
  startTime,
  endTime,
  enabled = true,
}: UseLessonBookingQuoteParams): UseLessonBookingQuoteResult {
  const tenantHandle = appRuntime.tenantHandle ?? undefined;
  const ready = !!clubId && !!instructorId && !!bayId && !!startTime && !!endTime;

  const query = useQuery({
    queryKey: ready
      ? queryKeys.lessons.bookingQuote(clubId, { instructorId, bayId, startTime, endTime })
      : queryKeys.disabled.lessons(),
    queryFn: () => {
      if (!clubId || !instructorId || !bayId || !startTime || !endTime || !tenantHandle) {
        throw new Error('The slot and tenantHandle are required');
      }
      return quoteLessonBooking({ instructorId, bayId, startTime, endTime }, { clubId, tenantHandle });
    },
    enabled: enabled && ready && !!tenantHandle,
    ...cachePresets.frequent,
    // One attempt per look: the step renders its own Retry.
    retry: false,
  });

  const result = query.data;

  return {
    ...query,
    quote: result?.success ? result.payload : null,
    hasAnswer: result !== undefined,
    failure: result && !result.success ? result : null,
  };
}
